/**
 * Page Atmosphere
 *
 * Wrapper component that assembles the correct atmospheric elements for each page.
 * Consuming pages simply declare which atmosphere they want via the `page` prop.
 */

import React from 'react';
import { AtmosphericLayers, type AtmosphereVariant } from './AtmosphericLayers';
import { DustMotes, type DustVariant } from './DustMotes';
import { PixelBorder, type BorderBehavior } from './PixelBorder';
import { SacredGeometry } from './SacredGeometry';
import { CompassRose } from './CompassRose';
import { HexGrid } from './HexGrid';
import { RuneGlyphs } from './RuneGlyphs';
import { StoneVeins } from './StoneVeins';

export type PageName = 'gallery' | 'create' | 'home' | 'battle' | 'lobby' | 'login' | 'generation' | 'challenge' | 'challenge-day' | 'adventure-hub' | 'adventure-lobby' | 'adventure-map' | 'about';

interface PageConfig {
  layers: AtmosphereVariant;
  motes: DustVariant;
  border: BorderBehavior;
  background: string[];
}

const PAGE_CONFIGS: Record<string, PageConfig> = {
  create: {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: ['SacredGeometry'],
  },
  home: {
    layers: 'standard',
    motes: 'lateral',
    border: 'scan',
    background: ['CompassRose', 'HexGrid'],
  },
  gallery: {
    layers: 'standard',
    motes: 'drift',
    border: 'hearth',
    background: [],
  },
  battle: {
    layers: 'standard',
    motes: 'lateral',
    border: 'scan',
    background: ['CompassRose'],
  },
  'adventure-hub': {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: ['HexGrid'],
  },
  'adventure-lobby': {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: [],
  },
  'adventure-map': {
    layers: 'standard',
    motes: 'drift',
    border: 'hearth',
    background: [],
  },
  about: {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: ['SacredGeometry'],
  },
  challenge: {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: [],
  },
  'challenge-day': {
    layers: 'dimmed',
    motes: 'stoneFall',
    border: 'tremor',
    background: ['RuneGlyphs', 'StoneVeins'],
  },
  login: {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: [],
  },
  generation: {
    layers: 'standard',
    motes: 'rise',
    border: 'breathe',
    background: [],
  },
};

function BackgroundElements({ elements }: { elements: string[] }) {
  return (
    <>
      {elements.map((el) => {
        switch (el) {
          case 'SacredGeometry': return <SacredGeometry key={el} />;
          case 'CompassRose': return <CompassRose key={el} />;
          case 'HexGrid': return <HexGrid key={el} />;
          case 'RuneGlyphs': return <RuneGlyphs key={el} />;
          case 'StoneVeins': return <StoneVeins key={el} />;
          default: return null;
        }
      })}
    </>
  );
}

interface PageAtmosphereProps {
  page: PageName;
  children: React.ReactNode;
}

export const PageAtmosphere: React.FC<PageAtmosphereProps> = ({ page, children }) => {
  const config = PAGE_CONFIGS[page];

  // If no config exists for this page yet, render children without atmosphere
  if (!config) {
    return <>{children}</>;
  }

  return (
    <>
      <AtmosphericLayers variant={config.layers} />
      <BackgroundElements elements={config.background} />
      <DustMotes variant={config.motes} />
      <PixelBorder behavior={config.border} />
      {children}
    </>
  );
};
